
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User } from "@supabase/supabase-js";
import { useToast } from "@/hooks/use-toast";

export const useAuthState = () => {
  const { toast } = useToast();
  const [user, setUser] = useState<User | null>(null);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [recoveryAttempts, setRecoveryAttempts] = useState(0);
  const MAX_RECOVERY_ATTEMPTS = 3;

  const handleError = useCallback((error: Error, context: string) => {
    setError(error.message);
    toast({
      title: "Error",
      description: `${context}: ${error.message}. Please refresh the page.`,
      variant: "destructive"
    });
  }, [toast]);

  const recoverSession = useCallback(async () => {
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) throw sessionError;
      
      if (session?.user) {
        setUser(session.user);
        setIsLoadingAuth(false);
        return true;
      } else {
        setUser(null);
        setIsLoadingAuth(false);
        return false;
      }
    } catch (error) {
      handleError(error instanceof Error ? error : new Error('Session recovery failed'), 'Session Recovery');
      return false;
    }
  }, [handleError]);

  useEffect(() => {
    let mounted = true;
    let authListener: any = null;

    const initializeAuth = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) throw sessionError;
        if (!mounted) return;

        setUser(session?.user || null);
        setIsInitialLoad(false);
        setIsLoadingAuth(false);

        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_, session) => {
          if (!mounted) return;
          setUser(session?.user || null);
        });

        authListener = subscription;

      } catch (error) {
        if (mounted) {
          handleError(
            error instanceof Error ? error : new Error('Failed to initialize authentication'),
            'Auth Initialization'
          );
        }
      }
    };

    initializeAuth();

    return () => {
      mounted = false;
      if (authListener) {
        authListener.unsubscribe();
      }
    };
  }, [handleError]);

  return {
    user,
    isLoadingAuth,
    error,
    recoveryAttempts,
    setRecoveryAttempts,
    recoverSession,
    MAX_RECOVERY_ATTEMPTS
  };
};
